import React, { useState, useEffect } from 'react';
import { Upload, FileSpreadsheet, CheckCircle, XCircle, Users, TrendingUp } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../lib/utils';

export default function PerformanceImport() {
  const { hasRole } = useAuth();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [allStats, setAllStats] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    fetchAllStats();
  }, []);

  const fetchAllStats = async () => {
    try {
      const response = await api.get('/performance/all');
      setAllStats(response.data || []);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/performance/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResult(response.data);
      
      if (response.data.importes > 0) {
        toast.success(`${response.data.importes} statistique(s) importée(s)`);
        fetchAllStats();
      }
      
      if (response.data.erreurs > 0) {
        toast.warning(`${response.data.erreurs} erreur(s) lors de l'import`);
      }
    } catch (error) {
      console.error('Error importing:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100';
    if (score >= 50) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  if (!hasRole(['admin', 'rh', 'responsable'])) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Accès non autorisé</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="performance-import-page">
      <div>
        <h2 className="text-2xl font-semibold">Import des statistiques de performance</h2>
        <p className="text-slate-600">Importez un fichier Excel hebdomadaire pour mettre à jour les scores des chauffeurs</p>
      </div>

      {/* Import Section */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FileSpreadsheet size={20} />
          Importer un fichier
        </h3>
        
        <div className="mb-4 p-4 bg-blue-50 rounded-lg text-sm text-blue-800">
          <p className="font-medium mb-2">Format attendu du fichier :</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Fichier Excel (.xlsx, .xls) ou CSV</li>
            <li>Colonnes : MATRICULE ou NOM (pour identifier le chauffeur)</li>
            <li>Métriques : SPMO, LSP, SPMD, POD, CC, CE, COP, DDR, DSC</li>
            <li>Les valeurs en pourcentage peuvent inclure le symbole %</li>
          </ul>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex-1">
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-slate-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-lg file:border-0
                file:text-sm file:font-semibold
                file:bg-brand file:text-white
                hover:file:bg-brand-dark
                cursor-pointer"
              data-testid="file-input"
            />
          </div>
          <Button 
            onClick={handleImport} 
            disabled={!file || loading}
            className="bg-brand hover:bg-brand-dark"
            data-testid="import-button"
          >
            <Upload size={18} className="mr-2" />
            {loading ? 'Import en cours...' : 'Importer'}
          </Button>
        </div>

        {file && (
          <p className="mt-2 text-sm text-slate-600">
            Fichier sélectionné : <span className="font-medium">{file.name}</span>
          </p>
        )}

        {/* Import Result */}
        {result && (
          <div className="mt-6 p-4 rounded-lg border border-slate-200">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle size={20} />
                <span className="font-medium">{result.importes} importé(s)</span>
              </div>
              {result.erreurs > 0 && (
                <div className="flex items-center gap-2 text-red-600">
                  <XCircle size={20} />
                  <span className="font-medium">{result.erreurs} erreur(s)</span>
                </div>
              )}
            </div>
            
            {result.details_erreurs && result.details_erreurs.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-slate-600 mb-2">Détails des erreurs :</p>
                <div className="max-h-40 overflow-y-auto">
                  {result.details_erreurs.map((err, idx) => (
                    <div key={idx} className="text-sm text-red-600 py-1">
                      Ligne {err.ligne}: {err.erreur}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Current Stats Overview */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <TrendingUp size={20} />
          Dernières statistiques par chauffeur
        </h3>

        {loadingStats ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
          </div>
        ) : allStats.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-2">Chauffeur</th>
                  <th className="text-center py-3 px-2">Score</th>
                  <th className="text-center py-3 px-2">SPMO</th>
                  <th className="text-center py-3 px-2">LSP</th>
                  <th className="text-center py-3 px-2">SPMD</th>
                  <th className="text-center py-3 px-2">POD</th>
                  <th className="text-right py-3 px-2">Mise à jour</th>
                </tr>
              </thead>
              <tbody>
                {allStats.map((stat) => (
                  <tr key={stat.id} className="border-b hover:bg-slate-50">
                    <td className="py-3 px-2 font-medium">{stat.employee_id}</td>
                    <td className="py-3 px-2 text-center">
                      <span className={`px-2 py-1 rounded-lg font-bold ${getScoreColor(stat.score_global)}`}>
                        {stat.score_global}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center">{stat.spmo?.toFixed(1)}%</td>
                    <td className="py-3 px-2 text-center">{stat.lsp?.toFixed(1)}%</td>
                    <td className="py-3 px-2 text-center">{stat.spmd?.toFixed(1)}%</td>
                    <td className="py-3 px-2 text-center">{stat.pod}</td>
                    <td className="py-3 px-2 text-right text-slate-600">
                      {formatDate(stat.imported_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <Users size={48} className="mx-auto mb-3 opacity-30" />
            <p>Aucune statistique importée</p>
            <p className="text-sm">Importez un fichier Excel pour commencer</p>
          </div>
        )}
      </div>
    </div>
  );
}
